import { Route } from "@angular/router";
import { FlexiComponent } from "./components/flexi.component";
import { AdminTabGuard, AuthGuard } from "src/app/_guards";
import { FeatureDocumentComponent } from "../feature-documents/feature-document.component";
import { DocumentListResolver } from "../feature-documents/feature-document.service.resolver";
import { OHSPlanComponent } from "./components/ohs-plan.component";
import { MyHSEAdvisor } from "./components/my-hse-advisor.component";
import { FlexiResolver, OHSDataResolver } from "./services/flexi.resolver.service";
// Risk Analysis is the Follow-ups module surfaced as a Flexi tab — same
// components, same resolvers, same feature id (138). Nothing is duplicated
// here; only the route is mounted a second time under /Flexi.
import {
    RiskAnalysisTopicCachedResolver,
    RiskAnalysisEditResolver,
    RiskAnalysisListResolver,
    ApprovalNoticesResolver,
    RiskAnalysisAdminResolver,
    RiskAnalysisCategoryResolver,
    RiskAnalysisLogResolver,
    RiskAnalysisTopicForAdminResolver,
    FollowupDepartmentForAdminResolver,
    UserForAdminResolver
} from "../follow-ups/services/risk-analysis.resolver.service";
import { RiskAnalysisComponent } from "../follow-ups/components/riskanalysis.component";
import { RiskAnalysisListComponent } from "../follow-ups/components/riskanalysis-list.component";
import { NewRiskAnalysisComponent } from "../follow-ups/components/riskanalysis-new.component";
import { NewRiskPortalComponent } from "../follow-ups/components/risk-new-portal.component";
import { AllDepartmentResolver } from "../follow-ups/services/deviation.resolver.service";
import { DepartmentCachedListResolver } from "../follow-ups/services/activities.resolver.service";
import {
    BusinessCacheResolver,
    CompanyNameResolver,
    RiskListResolver
} from "../businesses/services/business.resolver.service";
// The businesses/ resolver of the same name reads route.parent.params['bid'],
// a param that only exists under the business-detail routes; here it resolves
// to NaN. followups.routing.ts uses this role-aware one, so Flexi does too.
import { EmployeeContactUserResolver } from "../users/services/user.resolver.service";
import { OnboardingContainerComponent } from "./onboarding/onboarding-container.component";
import { AdditionalResourcesComponent } from "./onboarding/additional-resources.component";
import { BookingPortalFormComponent } from "./booking-portal/booking-portal-form.component";
import { FlexiOnboardingResolver } from "./onboarding/flexi-onboarding.service";
import { ComplianceContainerComponent } from "./compliance/compliance-container.component";
import { FlexiComplianceResolver } from "./compliance/flexi-compliance.service";
import { TemplatesListComponent } from "./compliance/templates/templates-list.component";
import { TemplatesEditorComponent } from "./compliance/templates/templates-editor.component";
import { TemplateWizardComponent } from "./compliance/templates/template-wizard.component";
import { ActionPointsListComponent } from "./compliance/action-points-list.component";
import { CustomerRequestFormComponent } from "./customer-request/customer-request-form.component";
import { FlexiAdvisorSettingsComponent } from "./advisor-settings/flexi-advisor-settings.component";
import { SafetyRoundWorkspaceComponent, safetyRoundDeviationsGuard } from './safety-rounds/safety-round-workspace.component';
import { SafetyRoundDeviationsComponent } from './safety-rounds/safety-round-deviations.component';
import { SafetyRoundListComponent } from "./safety-rounds/safety-round-list.component";
import { SafetyRoundComponent } from "./safety-rounds/safety-round.component";
import { SafetyRoundTemplateLibraryComponent } from "./safety-rounds/templates/safety-round-template-library.component";
import { SafetyRoundTemplateEditorComponent } from "./safety-rounds/templates/safety-round-template-editor.component";
import { ExposureRegisterOverviewComponent } from "./exposure-register/exposure-register-overview.component";
import { ExposureAssessmentComponent } from "./exposure-register/exposure-assessment.component";
import { ExposureTemplateLibraryComponent } from "./exposure-register/templates/exposure-template-library.component";
import { ExposureTemplateEditorComponent } from "./exposure-register/templates/exposure-template-editor.component";

export const flexiRoutes: Route[] = [{
    path: 'Flexi',
    component: FlexiComponent,
    canActivate: [AuthGuard],
    children: [{
        path: 'document/list',
        component: FeatureDocumentComponent,
        resolve: {
            list: DocumentListResolver
        }
    },
    {
        path: 'OHS-plan',
        component: OHSPlanComponent,
        canActivate: [AuthGuard],
        resolve: {
            OhsData: OHSDataResolver
        }
    },
    {
        path: 'HSE-advisor',
        component: MyHSEAdvisor,
        canActivate: [AuthGuard],
        resolve: {
            list: FlexiResolver
        }
    },
    // Mirrors the risk-analysis block in followups.routing.ts. The pre-#5353
    // version of this file declared 'risk-analysis' twice; Angular matches in
    // declaration order, so the second copy was dead and its 'newrisk' /
    // 'edit-risk' children were unreachable. Restored here as a single block
    // carrying all four children.
    {
        path: 'risk-analysis',
        component: RiskAnalysisComponent,
        canActivate: [AuthGuard],
        children: [
            {
                path: '',
                component: RiskAnalysisListComponent,
                resolve: {
                    detail: RiskAnalysisEditResolver,
                    approvalNotices: ApprovalNoticesResolver,
                    list: RiskAnalysisListResolver,
                    departments: AllDepartmentResolver,
                    adminlist: RiskAnalysisAdminResolver,
                    topic: RiskAnalysisTopicCachedResolver,
                    business: CompanyNameResolver,
                    Risklist: RiskListResolver,
                    category: RiskAnalysisCategoryResolver
                }
            },
            {
                path: 'newrisk',
                component: NewRiskAnalysisComponent,
                resolve: {
                    department: DepartmentCachedListResolver,
                    topic: RiskAnalysisTopicCachedResolver,
                    user: EmployeeContactUserResolver,
                    category: RiskAnalysisCategoryResolver,
                    business: CompanyNameResolver
                }
            },
            {
                path: 'edit-risk/:rid',
                component: NewRiskAnalysisComponent,
                resolve: {
                    detail: RiskAnalysisEditResolver,
                    department: DepartmentCachedListResolver,
                    topic: RiskAnalysisTopicCachedResolver,
                    user: EmployeeContactUserResolver,
                    log: RiskAnalysisLogResolver,
                    adminTopic: RiskAnalysisTopicForAdminResolver,
                    adminDepartment: FollowupDepartmentForAdminResolver,
                    usersAdmin: UserForAdminResolver,
                    category: RiskAnalysisCategoryResolver,
                    business: CompanyNameResolver
                }
            },
            {
                path: 'riskportal',
                component: NewRiskPortalComponent,
                resolve: {
                    business: BusinessCacheResolver
                }
            }
        ]
    },
    // The bare route is the CSM's history grid (customers still get the
    // read-only final view there); ':id' opens one form record in the wizard.
    // Both are served by the same container — the resolver reads the param and
    // decides which view the container renders.
    {
        path: 'responsibilities-and-duties',
        component: OnboardingContainerComponent,
        canActivate: [AuthGuard],
        resolve: {
            onboarding: FlexiOnboardingResolver
        }
    },
    // 'new' MUST precede ':id' — Angular matches in declaration order and
    // ':id' would happily swallow the literal 'new' segment.
    {
        path: 'responsibilities-and-duties/new',
        component: OnboardingContainerComponent,
        canActivate: [AuthGuard],
        data: { draft: true },
        resolve: {
            onboarding: FlexiOnboardingResolver
        }
    },
    {
        path: 'responsibilities-and-duties/:id',
        component: OnboardingContainerComponent,
        canActivate: [AuthGuard],
        resolve: {
            onboarding: FlexiOnboardingResolver
        }
    },
    {
        path: 'additional-resources',
        component: AdditionalResourcesComponent,
        canActivate: [AuthGuard]
    },
    {
        path: 'compliance',
        canActivate: [AuthGuard],
        children: [
            {
                path: '',
                component: ComplianceContainerComponent,
                canActivate: [AuthGuard],
                resolve: {
                    compliance: FlexiComplianceResolver
                }
            },
            {
                path: 'templates',
                component: TemplatesListComponent,
                canActivate: [AuthGuard]
            },
            // 'templates/new' MUST precede 'templates/:id' — Angular
            // matches in declaration order and ':id' would happily
            // swallow the literal 'new' segment.
            {
                path: 'templates/new',
                component: TemplateWizardComponent,
                canActivate: [AuthGuard]
            },
            {
                path: 'templates/:id',
                component: TemplatesEditorComponent,
                canActivate: [AuthGuard]
            },
            // Declared last so the literal 'templates' routes above win; ':id'
            // would otherwise swallow that segment. Opens one review — the live
            // one in the rating view, an archived one read-only.
            {
                path: ':id',
                component: ComplianceContainerComponent,
                canActivate: [AuthGuard],
                resolve: {
                    compliance: FlexiComplianceResolver
                }
            }
        ]
    },
    { path: 'deviations', pathMatch: 'full', redirectTo: 'safety-rounds/deviations' },
    // Safety rounds (230).
    {
        path: 'safety-rounds',
        component: SafetyRoundWorkspaceComponent,
        canActivate: [AuthGuard],
        children: [
            { path: '', pathMatch: 'full', component: SafetyRoundListComponent },
            { path: 'deviations', component: SafetyRoundDeviationsComponent, canActivate: [safetyRoundDeviationsGuard] },
            { path: ':roundId', component: SafetyRoundComponent }
        ]
    },
    // Exposure Register (231). A componentless parent, so the overview and
    // one assessment are siblings. The wizard step travels as ?step=1..4;
    // without it an assessment opens at its stored CurrentStep.
    {
        path: 'exposure-register',
        canActivate: [AuthGuard],
        children: [
            { path: '', pathMatch: 'full', component: ExposureRegisterOverviewComponent },
            { path: ':assessmentId', component: ExposureAssessmentComponent }
        ]
    },
    {
        path: 'action-points',
        component: ActionPointsListComponent,
        canActivate: [AuthGuard]
    },
    {
        path: 'additional-modules',
        component: CustomerRequestFormComponent,
        canActivate: [AuthGuard],
        data: { requestType: 1 }
    },
    {
        path: 'additional-services',
        component: CustomerRequestFormComponent,
        canActivate: [AuthGuard],
        data: { requestType: 0 }
    },
    {
        path: 'booking-portal',
        component: BookingPortalFormComponent,
        canActivate: [AuthGuard]
    },
    {
        // Admin mode only. AdminTabGuard also refuses an admin in R-mode, who
        // is working inside one business rather than across the application.
        path: 'settings',
        component: FlexiAdvisorSettingsComponent,
        canActivate: [AuthGuard, AdminTabGuard]
    },
    // The safety-round template library and editor live under the same
    // Settings tab (218) as siblings of the advisor page, so the advisor route
    // above is untouched. Same guard: Admin mode only.
    {
        path: 'settings/safety-round-templates',
        component: SafetyRoundTemplateLibraryComponent,
        canActivate: [AuthGuard, AdminTabGuard]
    },
    {
        path: 'settings/safety-round-templates/:templateId',
        component: SafetyRoundTemplateEditorComponent,
        canActivate: [AuthGuard, AdminTabGuard]
    },
    // The exposure assessment template library and editor, beside the
    // safety-round ones under the same Settings tab. Admin mode only.
    {
        path: 'settings/exposure-templates',
        component: ExposureTemplateLibraryComponent,
        canActivate: [AuthGuard, AdminTabGuard]
    },
    {
        path: 'settings/exposure-templates/:templateId',
        component: ExposureTemplateEditorComponent,
        canActivate: [AuthGuard, AdminTabGuard]
    }
    ]
}]
